import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ConfirmAccount from './components/auth/ConfirmAccount';
import Onboarding from './components/onboarding/Onboarding';
import Dashboard from './components/dashboard/Dashboard';
import ProjectManagement from './components/projects/ProjectManagement';
import MethodologySelector from './components/methodology/MethodologySelector';
import SprintView from './components/sprints/SprintView';
import BacklogView from './components/backlog/BacklogView';
import TaskModule from './components/tasks/TaskModule';
import CalendarView from './components/calendar/CalendarView';
import TeamManagement from './components/teams/TeamManagement';
import Notifications from './components/notifications/Notifications';
import Reports from './components/reports/Reports';
import Settings from './components/settings/Settings';
import Pricing from './components/pricing/Pricing';
import Plans from './components/pricing/Plans';
import Checkout from './components/pricing/Checkout';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProjectProvider } from './contexts/ProjectContext';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProjectProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  } 
                />
                <Route 
                  path="/register" 
                  element={
                    <PublicRoute>
                      <Register />
                    </PublicRoute>
                  } 
                />
                
                {/* Confirm Account Route (No Protection) */}
                <Route 
                  path="/confirm" 
                  element={<ConfirmAccount />} 
                />
                
                {/* Pricing Route (Public) */}
                <Route 
                  path="/pricing" 
                  element={<Pricing />} 
                />
                
                {/* Plans Route (Public) */}
                <Route 
                  path="/plans" 
                  element={<Plans />} 
                />
                
                {/* Checkout Route (Public) */}
                <Route 
                  path="/checkout" 
                  element={<Checkout />} 
                />
                
                {/* Protected Routes */}
                <Route 
                  path="/onboarding" 
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/methodology" 
                  element={
                    <ProtectedRoute>
                      <MethodologySelector />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/projects" 
                  element={
                    <ProtectedRoute>
                      <ProjectManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/sprints" 
                  element={
                    <ProtectedRoute>
                      <SprintView />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/backlog" 
                  element={
                    <ProtectedRoute>
                      <BacklogView />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/tasks" 
                  element={
                    <ProtectedRoute>
                      <TaskModule />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/calendar" 
                  element={
                    <ProtectedRoute>
                      <CalendarView />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/teams" 
                  element={
                    <ProtectedRoute>
                      <TeamManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/notifications" 
                  element={
                    <ProtectedRoute>
                      <Notifications />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/reports" 
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Default Route */}
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
              
              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'var(--toast-bg)',
                    color: 'var(--toast-color)',
                  },
                  success: {
                    iconTheme: {
                      primary: '#22c55e',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </ProjectProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
